import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V3 = '#51158C';

const RESEND_COOLDOWN = 30; // seconds

const VerifyOtp = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email;
  const name = location.state?.name;

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) navigate('/register');
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleDigitChange = (i, value) => {
    const v = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = e => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    setDigits(pasted.padEnd(6, '').split('').slice(0, 6));
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const otp = digits.join('');
    if (otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const { data } = await API.post('/auth/verify-otp', { email, otp });
      login(data, data.token);
      toast.success(`Welcome to ThiranX, ${data.name}! 🎉`);
      navigate('/courses');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      const { data } = await API.post('/auth/resend-otp', { email });
      toast.success(data.message || 'New code sent!');
      setCooldown(RESEND_COOLDOWN);
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not resend code');
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 16px 60px', background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <div style={{ position: 'absolute', bottom: '-200px', right: '-200px', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, #2B005744 0%, transparent 70%)` }} />
        <div style={{ position: 'absolute', top: '-200px', left: '-100px', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${V1}22 0%, transparent 70%)` }} />
      </div>

      <div style={{ width: '100%', maxWidth: 460, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 32, color: 'var(--v1)', marginBottom: 8 }}>
            Thiran<span style={{ color: 'var(--text)' }}>X</span>
          </div>
          <h2 style={{ fontSize: 'clamp(22px,5vw,28px)', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8, color: 'var(--text)' }}>Verify Your Email</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.6 }}>
            {name ? `Hey ${name}, we've` : "We've"} sent a 6-digit code to<br />
            <strong style={{ color: 'var(--text)' }}>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card" style={{
          background: 'var(--card-solid)', border: `1px solid var(--border)`, borderRadius: 20, padding: 32,
          boxShadow: 'var(--shadow-md)'
        }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 24 }} onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => (inputRefs.current[i] = el)}
                value={d}
                onChange={e => handleDigitChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                inputMode="numeric"
                maxLength={1}
                autoFocus={i === 0}
                style={{
                  width: 46, height: 54, textAlign: 'center', fontSize: 22, fontWeight: 800,
                  background: 'var(--bg3)', border: `1.5px solid var(--border)`, borderRadius: 10,
                  color: 'var(--text)', fontFamily: "'Satoshi','DM Sans',sans-serif",
                  transition: 'all 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = `${V1}77`}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            ))}
          </div>

          <button
            type="submit" disabled={loading}
            className="btn btn-primary"
            style={{
              width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px',
              fontWeight: 700, background: `linear-gradient(135deg,${V1},${V3})`,
              border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1, transition: 'all 0.2s'
            }}
          >
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 20, color: 'var(--muted)', fontSize: 14 }}>
            Didn't get the code?{' '}
            <button
              type="button" onClick={handleResend} disabled={resending || cooldown > 0}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: cooldown > 0 ? 'var(--muted)' : 'var(--v1)',
                fontWeight: 700, cursor: cooldown > 0 ? 'default' : 'pointer',
              }}
            >
              {resending ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
          </p>
        </form>

        <div style={{ marginTop: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          <Link to="/register" style={{ color: 'var(--v1)', textDecoration: 'none', fontWeight: 600 }}>← Back to Register</Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
